import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {ReelEngineEvent} from "@nolimitcity/slot-game/bin/core/engines/reel/event/ReelEngineEvent";
import {TimelineMax} from "gsap";
import {ReelEngineController, ReelState} from "@nolimitcity/slot-game/bin/core/engines/reel/ReelEngineController";
import {TimeCheckPoints} from "@nolimitcity/slot-game/bin/core/utils/TimeCheckPoints";
import {Presentation, PresentationState} from "@nolimitcity/slot-game/bin/core/gamecontroller/presentations/Presentation";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {WGEngineManager} from "../../engine/WGEngineManager";
import {WGParsedGameData} from "../../server/data/WGParsedGameData";
import {WarriorGraveyardGameModuleConfig} from "../../WarriorGraveyardGameModuleConfig";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {SoundManager} from "../../resource/sound/SoundManager";
import {WGSoundConfig} from "../../WGSoundConfig";
import {WGGameConfig} from "../../WGGameConfig";
import {WGEngineController} from "../../engine/WGEngineController";
import {GameModeEvent} from "@nolimitcity/slot-game/bin/core/gamemode/event/GameModeEvent";
import {WGSymbolData} from "../../engine/Symbol/WGSymbolData";
import {WGSpriteSymbol} from "../../engine/Symbol/WGSpriteSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";

export class ReelStopPresentation extends Presentation {
    protected _gameData: WGParsedGameData | undefined;
    protected animTl: TimelineMax;
    private _scatterSequenceonReels: number[] = []; // length will determine total number of scatter on reel
    private nearWinActivated: boolean;
    private _scatterAnticipationSoundPlaying: boolean = false;
    private _warriorAnticiaptionSoundPlaying: boolean = false;
    private _bigWinAnticiaptionSoundPlaying: boolean = false;
    private _antipationTimings: number[] = [0, 0, 1.8, 2, 2.4, 2.0]; // 5th index updated to fix #292
    // private triggeredWarriorSymbolForAnticipation: string = "";
    private triggeredWarriorSymbolForAnticipation: string[] = [];
    private _playingOnReelId: number = 0;

    public addEventListeners() {
        EventHandler.addEventListener(this, ReelEngineEvent.REEL_STOP_COMPLETED, (event: GameEvent) => this.playStopAnimation(event.params[0]));
        EventHandler.addEventListener(this, ReelEngineEvent.REEL_STOP_SPIN_BOUNCE_STARTED, (event: GameEvent) => this.playBounceAnimation(event.params[0]));
    }

    public checkForActivation(data: WGParsedGameData): boolean {
        return data.mode == GameMode.NORMAL || data.mode == GameMode.FREESPIN;
    }

    public isActive(): boolean {
        return (WGEngineManager.isPlaying) && super.isActive();
    }

    public setParsedGamedData(data: WGParsedGameData): void {
        if (this._gameData) return;

        this.animTl = Helper.timeLineKiller(this.animTl, new TimelineMax());
        this._gameData = data;
        this._playingOnReelId = 0;
        this.triggeredWarriorSymbolForAnticipation = [];
        this.triggeredWarriorSymbolForAnticipation = [];
        WarriorGraveyardGameModuleConfig.anticipationAnimation._triggerWarriorSymbol = data.triggerWarriorSymbol;
        WarriorGraveyardGameModuleConfig.anticipationAnimation._activeWarriorReels = data.activeWarriorSymbolReel;
        // did it for anticipation timings in case of super fs
        this._antipationTimings[this._antipationTimings.length - 1] = this.scatterUntil(4) === 4 ? 4 : 2.0;
        this.nearWinActivated = (this._gameData.nearWinReels.findIndex(v => v) >= 0 && this._gameData?.mode === GameMode.NORMAL) ||
            this._gameData.nearWinWarrior.findIndex(v => v) >= 0 || this._gameData.nearWinBigWinReels.findIndex(v => v) >= 0;
        // get scatter reels with sequence to play different sounds for each scatter
        this._scatterSequenceonReels = [];
        data.scatterPositions?.forEach((value: number, index: number) => {
            if (value > -1)
                this._scatterSequenceonReels.push(index);
        });
        WGGameConfig.NO_WIN_ALPHA = this.getAlphaValueForNowinSymbols(data);
    }

    /**
     * to fix #168
     * @param data
     */
    private getAlphaValueForNowinSymbols(data: WGParsedGameData): number {
        let retVal = 0
        switch (data.currentWinRatioConfig?.text) {
            case "Tiny Win":
                retVal = 0.4;
                break;
            case "Small Win":
                retVal = 0.5;
                break;
            default:
                retVal = 0.7;
                break
        }
        return retVal;

    }

    public preStart(): void {
        for (let i = 0; i < WGEngineManager.AllReels.length; i++) {
            WGEngineManager.AllReels[i].createStop();
        }
    }

    public getPresentation(): void {
        if (WGEngineManager.findFirstReelWithState(ReelState.STEP) >= 0)
            this.normalStop();
        else {
            // Add delay if there are reels that still stopping.
            if (WGEngineManager.getStoppingReel() >= 0) {
                this.pTimeline.to(this, WGEngineManager.getReel(WGEngineManager.getStoppingReel()).getPlayTime(), {});
            }
            // Add delay if there is an animation element ongoing.
            else if (this.animTl && this.animTl.isActive()) {
                this.pTimeline.to(this, this.animTl.totalDuration() - this.animTl.totalTime(), {});
            }
        }
    }

    protected calculateStopDelays(): number[] {
        let delay: number = 0.01;

        const delays: number[] = [];
        const timeRes: number[] = ArrayHelper.initArrayWithValues(WGEngineManager.AllReels.length, (i: number) => WGEngineManager.getReel(i).getStopDelay());

        for (let reel = 0; reel < WGEngineManager.AllReels.length; reel++) {
            const rc: ReelEngineController = WGEngineManager.getReel(reel);
            if (!rc.isActive || rc.state.isIdle) {
                delays.push(0);
                continue;
            }
            delay += timeRes[reel];
            delays.push(delay);
        }
        return delays;
    }

    protected normalStop(delays: number[] = this.calculateStopDelays()): void {
        if (!WGEngineManager.isQuickStop) {
            delays = this._gameData!.mode == GameMode.NORMAL ? this.delayModifier(delays) : this.delayFSModifier(delays);
        }

        this.pTimeline.add(() => {
        }, WGEngineManager.stopReels(delays));
    }

    /**
     * added this function to add delay for scatter anticipation animation
     * @param delays
     */
    private delayFSModifier(delays: number[]): number[] {
        let delay: number = 0;
        const winPossibleDelay: number = GameSetting.isFastSpin ? 0.3 : 0.55;
        const fsDelay: number = GameSetting.isFastSpin ? 0.2 : 0.4;
        for (let reel = 0; reel < WGEngineManager.AllReels.length; reel++) {
            if (delays[reel] == 0) continue;

            if (!WGEngineManager.getReel(reel).isActive || WGEngineManager.getReel(reel).state.isIdle) {
                continue;
            }
            if (this._gameData!.nearWinWarrior[reel]) {
                delay = delays[reel - 1] + this._antipationTimings[reel];
            } else if (this._gameData!.nearWinBigWinReels[reel]) {
                const final_delay_bigwin = (GameSetting.isFastSpin) ? this._antipationTimings[reel] * 0.5 : this._antipationTimings[reel] * 0.7;
                delay = delays[reel - 1] + final_delay_bigwin;
            } else if (this._gameData!.WildReelPositions[reel] >= 0) {
                delay += winPossibleDelay;
            } else if (!this.nearWinActivated && this._gameData?.singleWin) {
                delay += (this._gameData!.longestWin > reel) ? winPossibleDelay : 0; // updated to fix #141
            } else if (reel > 0 && this._scatterSequenceonReels.includes(reel) && this._scatterSequenceonReels.includes(reel - 1)) {
                delay += 0.5; // to fix #310
            }
            delays[reel] += delay;
        }
        return delays;
    }

    /**
     * added this function to add delay for scatter anticipation animation
     * @param delays
     */
    private delayModifier(delays: number[]): number[] {
        let delay: number = 0;
        const winPossibleDelay: number = 0.0;
        for (let reel = 0; reel < WGEngineManager.AllReels.length; reel++) {
            if (delays[reel] == 0) continue;

            if (!WGEngineManager.getReel(reel).isActive || WGEngineManager.getReel(reel).state.isIdle) {
                continue;
            }
            if ((this._gameData!.nearWinReels[reel] && this._gameData?.mode === GameMode.NORMAL) || this._gameData!.nearWinWarrior[reel]) {
                const final_delay = (GameSetting.isFastSpin || this._gameData?.wasFeatureBuy) ? this._antipationTimings[reel] * 0.5 : this._antipationTimings[reel];
                delay = delays[reel - 1] + final_delay;
            } else if (this._gameData!.nearWinBigWinReels[reel]) {
                const final_delay_bigwin = (GameSetting.isFastSpin) ? this._antipationTimings[reel] * 0.5 : this._antipationTimings[reel] * 0.7;
                delay = delays[reel - 1] + final_delay_bigwin;
            } else if (this._gameData!.WildReelPositions[reel] >= 0) {
                delay += winPossibleDelay;
            } else if (!this.nearWinActivated && this._gameData?.singleWin) {
                delay += (this._gameData!.longestWin > reel) ? winPossibleDelay : 0; // updated to fix #141
            } else if (reel > 0 && this._scatterSequenceonReels.includes(reel) && this._scatterSequenceonReels.includes(reel - 1)) {
                delay += 0.5; // to fix #310
            }
            delays[reel] += delay;
        }
        return delays;
    }

    protected nearWinBehaviouronLastReel(reelID: number): void {
        const stopdurr: number = WGEngineManager.getReel(reelID).getStopDuration();
        for (let reel: number = 0; reel <= reelID; reel++) {
            for (let j: number = 0; j < WGEngineManager.getReel(reel).windowAmount; j++) {
                let symPos: number = WGEngineManager.getReel(reel).AllWindowSymbols[j].posID + WGEngineManager.getReel(reel).statePosOffset;
                WGEngineManager.getReel(reel).getSymbol(symPos)!.attentionDarkness(0, stopdurr);
            }
            WGEngineManager.getReel(reel).scatterSymbols.forEach((s) => s.attentionDarkness(0, stopdurr));
        }
    }

    protected nearWinBehaviourBigwin(reelID: number): void {
        const stopdurr: number = WGEngineManager.getReel(reelID).getStopDuration();
        for (let reel: number = 0; reel <= reelID; reel++) {
            for (let j: number = 0; j < WGEngineManager.getReel(reel).windowAmount; j++) {
                let symPos: number = WGEngineManager.getReel(reel).AllWindowSymbols[j].posID + WGEngineManager.getReel(reel).statePosOffset;
                let sym = WGEngineManager.getReel(reel).getSymbol(symPos);
                if (!(sym?.isWild)) sym!.attentionDarkness(0.6, stopdurr);
            }
            WGEngineManager.getReel(reel).scatterSymbols.forEach((s) => s.attentionDarkness(0.6, stopdurr));
        }
    }

    // to fix #290
    protected nearWinBehaviourSuperFs(reelID: number): void {
        const stopdurr: number = WGEngineManager.getReel(reelID).getStopDuration();
        for (let reel: number = 0; reel <= reelID; reel++) {
            WGEngineManager.getReel(reel).scatterSymbols.forEach((s) => s.attentionDarkness(0.6, stopdurr));
        }
    }

    private nearWinBehaviourForScatter(reelId: number) {
        const darkIntensity: number = 0.6;
        const stopdurr: number = WGEngineManager.getReel(reelId).getStopDuration();
        if (this._gameData?.nearWinReels[reelId + 1] && reelId < 5) {
            for (let k: number = 0; k <= reelId; k++) {
                for (let j: number = 0; j < WGEngineManager.getReel(k).windowAmount; j++) {
                    let symPos: number = WGEngineManager.getReel(k).AllWindowSymbols[j].posID + WGEngineManager.getReel(k).statePosOffset;
                    const symbol: WGSpriteSymbol = WGEngineManager.getReel(k).getSymbol(symPos)!;
                    (!symbol.isScatter && !symbol.isWild) && symbol.attentionDarkness(darkIntensity, stopdurr);
                }
            }
        }
        if (this._gameData?.nearWinReels[reelId + 1]) {
            for (let k: number = 0; k <= reelId; k++) {
                WGEngineManager.getReel(k).scatterSymbols.forEach((s) => s.attentionDarkness(0, stopdurr));
            }
        }
    }

    /**
     * function to darken down all symbols except triggered warrior symbol
     * @param reelId
     */
    private nearWinBehaviourForWarrior(reelId: number) {
        const darkIntensity: number = 0.6;
        const stopdurr: number = WGEngineManager.getReel(reelId).getStopDuration();
        if (this._gameData?.nearWinWarrior[reelId + 1] && reelId < 5) {
            for (let k: number = 0; k <= reelId; k++) {
                if (this._gameData?.lockedWildReelsBefore[k]) continue;
                for (let j: number = 0; j < WGEngineManager.getReel(k).windowAmount; j++) {
                    let symPos: number = WGEngineManager.getReel(k).AllWindowSymbols[j].posID + WGEngineManager.getReel(k).statePosOffset;
                    const symbol: WGSpriteSymbol = WGEngineManager.getReel(k).getSymbol(symPos)!;
                    (!symbol.isWild) && symbol.attentionDarkness(darkIntensity, stopdurr)
                    if (this.triggeredWarriorSymbolForAnticipation.indexOf(symbol.symName) !== -1) symbol.attentionDarkness(0, stopdurr);
                }
            }
        }

        if (!this._gameData?.nearWinReels[reelId + 1]) {
            for (let k: number = 0; k <= reelId; k++) {
                WGEngineManager.getReel(k).scatterSymbols.forEach((s) => s.attentionDarkness(0.6, stopdurr));
            }
        }
    }

    private checkAndAddTriggerWarrioSymbol(reelId: number): void {
        const reels = this._gameData?.actualReels as string[][];
        let triggerWarriorSym: string[] = [...this.triggeredWarriorSymbolForAnticipation];
        let localSymbolData: string[] = [];

        for (let i = 0; i <= reelId; i++) {
            for (let j = 1; j < reels[i].length - 1; j++) {
                if (this._gameData!.lockedWildReelsBefore[i]) {
                    continue;
                }
                let sym = reels[i][j].split("*")[0];
                if (sym.indexOf("M") > -1) {
                    if (localSymbolData.indexOf(sym) > -1 && triggerWarriorSym.indexOf(sym) == -1) {
                        triggerWarriorSym.push(sym);
                    } else {
                        localSymbolData.push(sym);
                    }
                    break;
                }
            }
        }
        this.triggeredWarriorSymbolForAnticipation = triggerWarriorSym;
    }

    protected playBounceAnimation(reelID: number): void {
        if (this._gameData == undefined) return;

        if (reelID > 0 && reelID < 5) this.checkAndAddTriggerWarrioSymbol(reelID);
        this.playScatterLandingSounds(reelID);
        this.playWildLandingSound(reelID);
        if (this._bigWinAnticiaptionSoundPlaying && reelID < 5 && !(this._gameData.nearWinBigWinReels[reelID + 1])) {
            SoundManager.stopSoundEffect(WGSoundConfig.instance.BIG_WIN_ANTICIPATION, 500);
            if (this._gameData.mode === GameMode.FREESPIN) {
                SoundManager.fadeAmbienceLoop(1, 1000);
            }
        }


        if (this._gameData.mode === GameMode.FREESPIN) {
            this.playScatterPlusOneInFS(reelID);
        }
        if (this.scatterUntil(4) === 5 && reelID === 5 && this._gameData.mode === GameMode.NORMAL) {
            WarriorGraveyardGameModuleConfig.freeSpinLeftMeter.resetMultipliers();
            if (WGEngineManager.isQuickStop) { // to fix 219
                EventHandler.dispatchEvent(new GameEvent(GameModeEvent.CHANGE_MODE, {newMode: "FREESPIN"}));
                WarriorGraveyardGameModuleConfig.stickMuliplier.showReelAreaMulitplier();
            }
        }

        if (this._gameData.mode === GameMode.FREESPIN) {
            let reelSymbols: string[] = this._gameData.actualReels[reelID]; // #190
            // to fix #145
            for (let i = 1; i < reelSymbols.length - 1; i++) {
                const wildIndex: number = reelSymbols[i].indexOf("W");
                if (wildIndex > -1) {
                    const wildPostFix: string = reelSymbols[i].split("*")[1],
                        symId: number = ((wildPostFix !== "2") && (i !== 2)) ? (i) : (i - 1);
                    WarriorGraveyardGameModuleConfig.stickMuliplier.updateMulitplier(reelID, true, symId);
                    break;
                }
            }
        }
            const reelSymbols: string[] = this._gameData.actualReels[reelID]; // #190
            for (let i = reelSymbols.length - 2; i >= 1; i--) {
                const wildIndex: number = reelSymbols[i].indexOf("W");
                if (wildIndex > -1) {
                    const symId = i === 1 ? 1 : i + 1;
                    WarriorGraveyardGameModuleConfig.anticipationAnimation.playWildLandingAnimation(reelID, symId,
                        this._gameData.triggerWarriorPay || this._gameData.symbolWinStates[reelID].includes(true));
                    break;
                }
            }
        const nextReel: number = this._gameData.superFreespin ? this.getNextSpinningReel(reelID) : reelID + 1;
        if (!WGEngineManager.isQuickStop && reelID < 5) {

            // both in if becoz both can be played on same reel
            // this._gameData.mode===GameMode.NORMAL added to fix #199
            if (this._gameData.nearWinReels[nextReel] && this._gameData.mode === GameMode.NORMAL) {
                this.nearWinBehaviourForScatter(reelID);
                this.scatterAnticipation(reelID, nextReel);

            }
            if (this._gameData.nearWinWarrior[nextReel]) {
                this.playWarriorAnticipation(nextReel, reelID);
                this.nearWinBehaviourForWarrior(reelID);
            } else if (this._gameData.nearWinBigWinReels[nextReel] && !(this._gameData.nearWinReels[nextReel] && this._gameData.mode === GameMode.NORMAL)) {
                SoundManager.playSoundEffect(WGSoundConfig.instance.BIG_WIN_ANTICIPATION);
                if (this._gameData.mode === GameMode.FREESPIN) {
                    SoundManager.fadeAmbienceLoop(0.8, 1000);
                }
                this._bigWinAnticiaptionSoundPlaying = true;
                WGEngineManager.getReel(nextReel).Engine.activeTimeline.timeScale(1.2);
            }
        } else if (this.triggeredWarriorSymbolForAnticipation.length && reelID === 5 && !WGEngineManager.isQuickStop) {
            this.warriorAnticipationBlastOnLastReel(reelID);
        } else if (this._gameData.nearWinWarrior[nextReel] && this.triggeredWarriorSymbolForAnticipation && WGEngineManager.isQuickStop) {
            WarriorGraveyardGameModuleConfig.anticipationAnimation.litUpWarriorSymbol(this.triggeredWarriorSymbolForAnticipation, reelID);
        }

        if (this.nearWinActivated && WGEngineManager.isQuickStop || reelID == 5) {
            if (reelID === 5) {
                this.playWarriorfeatureSounds(reelID)
                this.nearWinBehaviouronLastReel(reelID);
                // on last reel bounce placed here to fix #262
                this.stopAllSoundsOnLastReelBounce();
            }
            // #144
            WarriorGraveyardGameModuleConfig.anticipationAnimation.stopDeathIsNear();
            WarriorGraveyardGameModuleConfig.anticipationAnimation.stopAndHideAttentions();
            // WarriorGraveyardGameModuleConfig.anticipationAnimation.stopAndHideBigWinAnticipation();
            WarriorGraveyardGameModuleConfig.anticipationAnimation.stopAndHideWarriorAttackAnimation();
            if (this.triggeredWarriorSymbolForAnticipation) {
                //  to fix #197 && #220
                if (WGEngineManager.isQuickStop) {
                    WarriorGraveyardGameModuleConfig.anticipationAnimation.litUpWarriorSymbol(this.triggeredWarriorSymbolForAnticipation, reelID);
                } else {
                    // this.animTl.add(() => {
                    //     // WarriorGraveyardGameModuleConfig.anticipationAnimation.hideAllWarriorFire();
                    // }, this.animTl.totalTime() + 0.5);
                }
            }
        }
        WGEngineManager.getReel(reelID).changeAlpha(0, WGEngineManager.getReel(reelID).getStopDuration() / 2);
        if (this.scatterOnLastReel(reelID)) {
            this.animTl.add(() => {
            }, this.animTl.totalTime() + 0.5);
        }
        // reel landing sound for every reel bounce
        SoundManager.playSoundEffect(WGSoundConfig.instance.REELS_DEFAULT_BOUNCE[reelID]);
    }

    private playWarriorAnticipation(nextReel: number, reelID: number): void {
        this.playWarriorfeatureSounds(reelID);
        this.triggeredWarriorSymbolForAnticipation && WarriorGraveyardGameModuleConfig.anticipationAnimation.litUpWarriorSymbol(this.triggeredWarriorSymbolForAnticipation, reelID);
        WarriorGraveyardGameModuleConfig.anticipationAnimation.playWarrior(nextReel);
        WGEngineManager.getReel(nextReel).Engine.activeTimeline.timeScale(1.2);
    }

    private playWarriorfeatureSounds(reelId: number): void {
        if (this._gameData!.nearWinWarrior[reelId]) {
            let isPlayLanding: boolean = false;
            let reel = this._gameData?.actualReels[reelId] as string[];
            for (let i = 0; i < reel.length; i++) {
                if (this.triggeredWarriorSymbolForAnticipation.includes(reel[i].split("*")[0])) {
                    isPlayLanding = true;
                    break;
                }
            }
            isPlayLanding && SoundManager.playSoundEffect(WGSoundConfig.instance.WARRIOR_LANDING);
        }

        if (!this._warriorAnticiaptionSoundPlaying && (reelId < 5)) {
            this._warriorAnticiaptionSoundPlaying = true;
            SoundManager.playSoundEffect(WGSoundConfig.instance.WARRIOR_ANTICIPATON, 100);
            let volume = this._gameData?.mode === GameMode.NORMAL ? 0 : 0.5; // fix for #252
            SoundManager.fadeAmbienceLoop(volume, 1000);
        }
    }

    private stopAllSoundsOnLastReelBounce(): void {
        if (this._gameData == undefined) return;

        SoundManager.stopSoundEffect(WGSoundConfig.instance.REELS_SPINNING);
        if (this._scatterAnticipationSoundPlaying) {
            SoundManager.stopSoundEffect(WGSoundConfig.instance.SCATTER_ANTICIPATION);
            SoundManager.fadeAmbienceLoop(1, 1000);
            this._scatterAnticipationSoundPlaying = false;
            if (this._gameData.freespinTriggeredThisSpin)
                SoundManager.playSoundEffect(WGSoundConfig.instance.SCATTER_ANTICIPATION_END_WIN);
            else
                SoundManager.playSoundEffect(WGSoundConfig.instance.SCATTER_ANTICIPATION_END_FAIL);
        }
        if (this._warriorAnticiaptionSoundPlaying) {
            SoundManager.stopSoundEffect(WGSoundConfig.instance.WARRIOR_ANTICIPATON);
            SoundManager.fadeAmbienceLoop(1, 1000);
            this._warriorAnticiaptionSoundPlaying = false;
            if (this._gameData.triggerWarriorPay)
                SoundManager.playSoundEffect(WGSoundConfig.instance.WARRIOR_ANTICIPATON_WIN);
            else
                SoundManager.playSoundEffect(WGSoundConfig.instance.WARRIOR_ANTICIPATON_FAIL);
        }
        // to stop bigwin sound
        if (this._bigWinAnticiaptionSoundPlaying) {
            this._bigWinAnticiaptionSoundPlaying = false;
            SoundManager.stopSoundEffect(WGSoundConfig.instance.BIG_WIN_ANTICIPATION, 500);
            if (this._gameData.mode === GameMode.FREESPIN) {
                SoundManager.fadeAmbienceLoop(1, 1000);
            }
        }
    }

    /**
     * function to play scatter anticipation
     * @param reelID
     * @param nextReel
     */
    private scatterAnticipation(reelID: number, nextReel: number): void {
        // sound specific check
        if (!this._scatterAnticipationSoundPlaying) {
            this._scatterAnticipationSoundPlaying = true;
            SoundManager.fadeAmbienceLoop(0, 1000);
            SoundManager.playSoundEffect(WGSoundConfig.instance.SCATTER_ANTICIPATION, 100);
        }
        // super fs check
        if (this.scatterUntil(reelID) === 5 && reelID === 4) {
            WGEngineManager.getReel(nextReel).Engine.nextTimeline!.timeScale(1.2);
            const rc: WGEngineController = WGEngineManager.getReel(reelID);
            rc.changeAlpha(0, 0);
            WarriorGraveyardGameModuleConfig.anticipationAnimation.playSuperFsAnticipationAnimation();
            this.nearWinBehaviourSuperFs(reelID);
        }
        // normal anticipation for scatter check
        else {
            if (this._playingOnReelId < nextReel) {
                WarriorGraveyardGameModuleConfig.anticipationAnimation.playScatter(nextReel);
                this._playingOnReelId = nextReel;
                SoundManager.playSoundEffect(WGSoundConfig.instance.SCATTER_ANTICIPATION_FIRE);
                WGEngineManager.getReel(nextReel).Engine.activeTimeline.timeScale(1.2);
            }
        }
    }

    /**
     * handle warrio anticipation case on last reel
     * @param reelID
     */
    private warriorAnticipationBlastOnLastReel(reelID: number): void {
        if (this._gameData == undefined) return;
        let symIndex = -1;
        for (let i = 1; i < this._gameData.actualReels[reelID].length - 1; i++) {
            let symName = this._gameData.actualReels[reelID][i].split("*")[0];
            if (this.triggeredWarriorSymbolForAnticipation.indexOf(symName) > -1) {
                symIndex = i;
                break;
            }
        }
        if (symIndex > -1) {
            let blastFlash = WarriorGraveyardGameModuleConfig.anticipationAnimation.createFlash(reelID, symIndex);
            this.animTl.add(() => {
                blastFlash.visible = true
            });
            this.animTl.add(() => {
                this.triggeredWarriorSymbolForAnticipation && WarriorGraveyardGameModuleConfig.anticipationAnimation.litUpWarriorSymbol(this.triggeredWarriorSymbolForAnticipation, reelID);
            });

            this.animTl.fromTo(blastFlash, 0.1, {alpha: 0.7}, {
                alpha: 0.1, onComplete: () => {
                    blastFlash.visible = false;
                }
            });
            this.animTl.add(WarriorGraveyardGameModuleConfig.anticipationAnimation.warriorTextBasedOnLevels(1), 0);
        }
    }

    private getNextSpinningReel(prevReelId: number): number {
        for (let i = 0; i < WGGameConfig.instance.REEL_ENGINE_CONFIG.REEL_AMOUNT; i++) {
            if (i > prevReelId && !WGEngineManager.getReel(i).state.isIdle) { // updated to fix #269
                return i;
            }
        }
        return -1; // don't think this will ever occur
    }

    private prevSpinningReel(reelId: number): number {
        for (let i = reelId - 1; i > -1; i--) {
            if (!this._gameData?.lockedWildReelsBefore[i]) {
                return i;
            }
        }
        return -1; // don't think this will ever occur
    }

    private playScatterLandingSounds(reelId: number): void {
        if (this._scatterSequenceonReels.indexOf(reelId) > -1) {
            let soundIndex: number = this._scatterSequenceonReels.indexOf(reelId) > 2 ? 2 : this._scatterSequenceonReels.indexOf(reelId)
            SoundManager.playSoundEffect(WGSoundConfig.instance.SCATTER_BOUNCE_SOUNDS[soundIndex]);
        }
    }

    /**
     * funciton to playWildLandingSound
     */
    private playWildLandingSound(reelId: number): void {
        if (this._gameData!.WildReelPositions[reelId] >= 0) { // fixed #148
            SoundManager.playSoundEffect(WGSoundConfig.instance.WILD_LANDING);
        }
    }

    /**
     * function to get scatter count til the given reelId
     * @param reelID
     */
    private scatterUntil(reelID: number): number {
        let tmp: number = 0;
        for (let i = 0; i <= reelID; i++) {
            tmp += this._gameData!.scatterPositions![i] >= 0 ? 1 : 0;
        }
        return tmp;
    }

    protected playStopAnimation(reelID: number): void {
        if (this._gameData == undefined) return;

        if (WGEngineManager.getStoppingReel() == -1 && WGEngineManager.findFirstReelWithState(ReelState.STEP) == -1) {
            WarriorGraveyardGameModuleConfig.stickWildView._lockedReels = this._gameData.lockedWildReelsAfter;
            this.PresentationState.set(PresentationState.INACTIVE);
        }
    }

    private playScatterPlusOneInFS(reelId: number): void {
        if (this._gameData && this._gameData.scatterPositions![reelId] !== -1) {
            this.playRetriggerOnScatter(reelId, this._gameData.scatterPositions![reelId]);
            this.animTl.add(WarriorGraveyardGameModuleConfig.freeSpinLeftMeter.increaseFsMeterValueOnRetrigger(), 0);
        }
    }

    private playRetriggerOnScatter(reelId: number, symbolId: number) {
        let retriggerAnim: PIXI.spine.Spine = new PIXI.spine.Spine(GameResources.getSpineAsset("plusone_freespin"));
        retriggerAnim.visible = false;
        StageManager.getLayer(WGGameConfig.instance.LAYERS.REEL_OVERLAY.name).addChild(retriggerAnim);

        retriggerAnim.position.set((reelId * 120) + 60, ((symbolId % 3) + 1) * (199));
        this.animTl.add(() => {
            retriggerAnim.updateTransform();
            retriggerAnim.visible = true;
            SoundManager.playSoundEffect(WGSoundConfig.instance.FS_PLUS_ONE); // #177
        }, 0);
        this.animTl.add(new SpineTween(retriggerAnim, 0, "plusone_freespin"), 0);
    }

    private scatterOnLastReel(reelID: number): boolean {
        return this._gameData!.scatterReelPositions[reelID] >= 0;
    }

    // Checks if any reels are still stopping.
    public promiseSync(): boolean {
        if (this.animTl && this.animTl.isActive()) {
            return false;
        }
        return WGEngineManager.getStoppingReel() == -1;
    }

    public stopPresentation(): void {
        const reelIdWithWarrior: number[] = [];

        if (this.triggeredWarriorSymbolForAnticipation.length > 1 && this._gameData?.triggerWarriorPay) {
            WarriorGraveyardGameModuleConfig.anticipationAnimation.hideAllWarriorFire();
            WarriorGraveyardGameModuleConfig.anticipationAnimation._warriorAttackText.visible = false;
            WarriorGraveyardGameModuleConfig.anticipationAnimation._warriorAttackText.alpha = 0;
            WarriorGraveyardGameModuleConfig.anticipationAnimation.litUpWarriorSymbol([this._gameData.triggerWarriorSymbol], 5, true)
        } else if (!this._gameData?.triggerWarriorPay) {
            WarriorGraveyardGameModuleConfig.anticipationAnimation.hideAllWarriorFire();
            !(this._gameData?.singleWin) && WarriorGraveyardGameModuleConfig.anticipationAnimation.stopAllWildLanding();
        }

        // log aaded to investigate #220 diference b/w reels symbols of server and game reels.
        const reels = this._gameData?.actualReels;
        if (reels) {
            for (let i = 0; i < reels!.length; i++) {
                for (let j = 1; j < 4; j++) {
                    let serverSym = reels[i][j];
                    let clientSym = WGEngineManager.getReel(i).AllWindowSymbols[j - 1].symName;
                    if (WGSymbolData.stackSymbols.indexOf(clientSym) > -1) {
                        clientSym += "*" + (WGEngineManager.getReel(i).AllWindowSymbols[j - 1].stateType.state - 1).toString();
                    }
                    if (serverSym !== clientSym && !this._gameData?.lockedWildReelsBefore[i]) {
                        console.error(`Incorrect symbol on position \n reelID : ${i},\n  symbolId:  ${j},\n symbolOnReel: ${clientSym},\n symbol from server ${serverSym}`);
                    }
                }
            }
        }
        WGEngineManager.isPlaying = false;
        Logger.logDev(TimeCheckPoints.getCheckPointTime("Bet").toString());
        this._gameData = undefined;
    }
}
